import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, EyeIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, StarIcon } from '@heroicons/react/24/solid';
import PropTypes from 'prop-types';

const EnhancedProductCard = ({ 
  product, 
  onQuickView, 
  onAddToWishlist, 
  onAddToCart,
  isInWishlist = false 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const fragranceFamily = product.attributes?.fragrance_family || 'Floral';
  const intensity = Math.min(5, Math.max(0, Number(product.attributes?.intensity) || 3));
  const isBestSeller = (product.sales_count || 0) > 100;
  const createdAt = product.created_at || product.createdAt;
  const isNew = createdAt && !isNaN(new Date(createdAt).getTime()) && new Date(createdAt) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

  const handleQuickView = (e) => {
    e.preventDefault();
    onQuickView && onQuickView(product);
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    onAddToWishlist && onAddToWishlist(product);
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    onAddToCart && onAddToCart(product);
  };

  return (
    <div
      className="group relative bg-cream-50 rounded-sm overflow-hidden border border-cream-400/50 hover:border-gold-400/50 transition-colors duration-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Badges */}
      <div className="absolute top-2.5 right-2.5 z-10 flex flex-col gap-1.5">
        {isNew && (
          <span className="bg-charcoal-900 text-cream-100 text-xs font-medium px-2.5 py-1">
            חדש
          </span>
        )}
        {isBestSeller && (
          <span className="bg-gold-200 text-gold-700 text-xs font-medium px-2.5 py-1">
            רב מכר
          </span>
        )}
      </div>

      {/* Wishlist button */}
      <button
        onClick={handleWishlist}
        className="absolute top-2.5 left-2.5 z-10 bg-cream-100/90 p-1.5 hover:bg-cream-100 transition-colors duration-200"
        aria-label="הוסף לרשימת משאלות"
      >
        {isInWishlist ? (
          <HeartIconSolid className="h-4 w-4 text-red-500" />
        ) : (
          <HeartIcon className="h-4 w-4 text-charcoal-600 hover:text-red-500" />
        )}
      </button>

      {/* Product image */}
      <Link to={`/products/${product.id}`} className="block relative aspect-square overflow-hidden bg-cream-200">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-cream-200 animate-pulse" />
        )}
        
        <img
          src={product.images?.[0] || '/placeholder-product.jpg'}
          alt={product.name}
          className={`w-full h-full object-cover transition-all duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          } ${isHovered ? 'scale-[1.02]' : 'scale-100'}`}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
        />

        {/* Hover overlay with quick view */}
        <div
          className={`absolute inset-0 bg-charcoal-900/25 transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
            <button
              onClick={handleQuickView}
              className="bg-cream-100 text-charcoal-900 px-3 py-1.5 text-sm font-medium hover:bg-cream-200 transition-colors flex items-center gap-1.5"
            >
              <EyeIcon className="h-3.5 w-3.5" />
              צפייה מהירה
            </button>
            <button
              onClick={handleAddToCart}
              className="bg-charcoal-900 text-cream-100 px-3 py-1.5 text-sm font-medium hover:bg-gold-500 hover:text-charcoal-900 transition-colors flex items-center gap-1.5"
            >
              <ShoppingCartIcon className="h-3.5 w-3.5" />
              הוסף
            </button>
          </div>
        </div>
      </Link>

      {/* Product info */}
      <div className="p-4">
        {/* Fragrance family badge */}
        <div className="mb-2">
          <span className="inline-block bg-gold-100/60 text-gold-700 text-xs font-medium px-2 py-0.5">
            {fragranceFamily}
          </span>
        </div>

        {/* Product name */}
        <Link to={`/products/${product.id}`}>
          <h3 className="text-sm font-semibold text-charcoal-900 mb-2 line-clamp-2 hover:text-gold-600 transition-colors min-h-[2.5rem]">
            {product.name}
          </h3>
        </Link>

        {/* Rating */}
        {product.rating_average > 0 && (
          <div className="flex items-center mb-2">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <StarIcon
                  key={i}
                  className={`h-3.5 w-3.5 ${
                    i < Math.floor(product.rating_average)
                      ? 'text-gold-500 fill-current'
                      : 'text-cream-400'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-charcoal-600/60 mr-1.5">
              ({product.rating_count})
            </span>
          </div>
        )}

        {/* Intensity indicator */}
        <div className="flex items-center gap-1 mb-3">
          <span className="text-xs text-charcoal-600/60">עוצמה:</span>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 ${
                i < intensity
                  ? 'bg-gold-500'
                  : 'bg-cream-400'
              }`}
            />
          ))}
        </div>

        {/* Price and actions */}
        <div className="flex items-center justify-between pt-3 border-t border-cream-400/40">
          <div>
            <div className="text-lg font-bold text-charcoal-900">
              ₪{product.price_b2c ?? product.price ?? 0}
            </div>
            {product.price_b2b != null && product.price_b2b !== (product.price_b2c ?? product.price) && (
              <div className="text-xs text-charcoal-600/50 line-through">
                ₪{product.price_b2b}
              </div>
            )}
          </div>

          {/* Stock indicator */}
          {product.stock_quantity <= 10 && product.stock_quantity > 0 && (
            <span className="text-xs text-gold-700 font-medium bg-gold-100/60 px-2 py-0.5">
              נותרו {product.stock_quantity}
            </span>
          )}
          {product.stock_quantity === 0 && (
            <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-0.5">
              אזל המלאי
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

EnhancedProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price_b2c: PropTypes.number.isRequired,
    price_b2b: PropTypes.number,
    images: PropTypes.array,
    rating_average: PropTypes.number,
    rating_count: PropTypes.number,
    stock_quantity: PropTypes.number,
    sales_count: PropTypes.number,
    created_at: PropTypes.string,
    attributes: PropTypes.object,
  }).isRequired,
  onQuickView: PropTypes.func,
  onAddToWishlist: PropTypes.func,
  onAddToCart: PropTypes.func,
  isInWishlist: PropTypes.bool,
};

export default EnhancedProductCard;

